import SHA from 'sha.js';

import { Os, Referrer, Type } from '../models/events/basic-event.model';

import { getCookie } from '@core/application/use-cases/cookies/get-cookie.use-case';
import { CommonWindow, Inditex } from '@core/domain/models/common-window.model';
import { CookieGroup, UserConsentPoliciesValues } from '@core/domain/models/user-consent.models';
import { Resolutions } from '@layout/domain/models/layout.models';

/**
 * Saved values for each consent category
 * IMPORTANT: ID's (C000x) might be different according to your implementation
 * Standard ID's for OneTrust are the following:
    StrictlyNecessary = 'C0001'
    Analytics = 'C0002'
    FeaturesOrCustomization = 'C0003'
    Advertising = 'C0004'
    SocialMedia = 'C0005'
 *
 * @param cookieGroupMap
 * @returns string
 */
export const getConsentCategories = (cookieGroupMap: Record<UserConsentPoliciesValues, CookieGroup>): string => {
    let consentCategories = '';
    for (const [key, value] of Object.entries(cookieGroupMap ?? {})) {
        consentCategories += `${key}:${value.allowed ? '1' : '0'}`;
        if (Object.keys(cookieGroupMap).indexOf(key) !== Object.keys(cookieGroupMap).length - 1) {
            consentCategories += ',';
        }
    }

    return consentCategories || null;
};

/**
 * Returns the formatted date of the last setting of the cookies consent options
 */
export const getConsentedDate = (): string => {
    const cookieValue = getCookie(document.cookie, 'OptanonConsent');
    const propertyName = 'datestamp';

    if (cookieValue) {
        const properties = new URLSearchParams(cookieValue);
        const cookiePropertyValue = properties.get(propertyName);

        if (cookiePropertyValue) {
            // biome-ignore lint/nursery/useTopLevelRegex: Called once for each Zenit event, no performance impact
            const regex: RegExp = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{2}) (\d{4}) (\d{2}:\d{2}:\d{2}) (\w{3}) (\d{4}) \(.+\)/;
            const match: RegExpExecArray = regex.exec(cookiePropertyValue);

            if (match[0]) {
                const dateObj = new Date(match[0]);
                return dateObj.toISOString(); // Output format: "2025-03-25T10:05:17.000Z"
            }
        }
    }

    return null;
};

/**
 * Gets specific utm param from queryParams
 *
 * @param queryParams
 * @param param
 * @returns string
 */
export const getUtmParam = (inputUrl: string, param: string): string => {
    const url = new URL(inputUrl);
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    const _param = /(gclid)/.exec(param) ? param : `utm_${param}`;
    return url.searchParams.get(_param) || null;
};

/**
 *
 * @param language
 * @returns string
 */
export const getLanguage = (language: string): string => {
    return language.replace('-', '_');
};

/**
 * Gets the client device type from the resolution (mobile|tablet|desktop)
 *
 * @param resolution
 * @returns Type
 */
export const getType = (resolution: Resolutions): Type => {
    let device: Type;

    if (resolution === Resolutions.MOBILE) {
        device = Type.mobile;
    } else if (resolution === Resolutions.TABLET) {
        device = Type.tablet;
    } else {
        device = Type.desktop;
    }

    return device;
};

/**
 * Gets the OS type from the userAgent string
 *
 * @param userAgent
 * @returns Os
 */
export const getOs = (userAgent: string): Os => {
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(Android)\s(\S*)/.exec(userAgent)) return Os.android;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(iPhone|iPod|iPod touch);/.exec(userAgent)) return Os.ios;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(iPad;)/.exec(userAgent)) return Os.ipados;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(Macintosh;)/.exec(userAgent)) return Os.mac;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(Win64|Win32|Windows|Windows Phone\s(\S*));/.exec(userAgent)) return Os.windows;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(WebOS;)/.exec(userAgent)) return Os.android;
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    if (/(Linux)\s(\S*)/.exec(userAgent)) return Os.linux;
    return null;
};

/**
 * Gets the client device OS version, examples: 15.4.1, 9
 *
 * @param userAgent
 * @returns string
 */
export const getOsVersion = (userAgent: string): string => {
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    const match: string[] = /(Android|iPhone|iPad|iPod|webOS|BlackBerry|Windows Phone|Windows|Mac OS X|CPU OS|Linux)\s\w*\s*\d(\w|\.)+/.exec(userAgent);
    if (match) {
        const version = match[0].split(' ');
        return version[version.length - 1];
    }
    return null;
};

/**
 * Generates a string with the screen resolution from the screen object
 * @param screen
 */
export const getDisplayResolution = (screen: Screen): string => {
    return `${screen.width}x${screen.height}`;
};

/**
 * Returns the user type following the Zenit convention
 * @param userType Massimo Dutti domain user type
 */
export const getUserType = (userType: string): string => {
    return userType === 'R' ? 'registered' : 'guest';
};

/**
 * Returns the user id hashed with SHA256
 * @param userId User ID from the Massimo Dutti domain
 */
export const getUserIdToken = (userId: number): string => {
    if (userId && userId !== -1002) {
        return SHA('sha256').update(userId.toString()).digest('hex');
    }
    return null;
};

/**
 * Returns the document referrer if it is not empty, is not the same as the current domain and the referred event has not been sent
 * @param _document Document object reference
 * @param window Window object reference
 * @param referredSent Will be true if the referred event has been sent
 * @returns Referrer object with the referrer URL if it meets the conditions, otherwise null
 */
export const getReferrer = (_document: Document, window: CommonWindow, referredSent: boolean): Referrer => {
    if (!!_document.referrer && new URL(_document.referrer).host !== window.location.host && !referredSent) {
        return {
            url: _document.referrer,
        };
    }

    return null;
};

/**
 * Returns the current frontend app version if defined
 * @param inditex Inditex object reference
 */
export const getAppVersion = (inditex: Inditex): string => {
    return inditex?.iFrontVersion || null;
};
